/**
 * QueuePageBuilder — designs the WHOLE queue journey's customer pages (5–6
 * distinct pages) with the same engine as the Landing Pages editor. The header
 * arrows page between the journey's pages (operator feedback 2026-07-20: the
 * journey is several pages, not one); each page keeps its own blocks/theme.
 * Done returns every edited page's cfg keyed by step id; Cancel discards all.
 * Landing-page navigation and the queue-block palette were removed — the
 * page's functional core block is seeded automatically and can be restored
 * from the header chip if deleted.
 */
import { useMemo, useRef, useState } from "react";
import { Btn, Dropdown, Icon, IconBtn, T, tx } from "@v0y4ge/jrni-ui";
import { useBranding } from "../settings/useBranding";
import { useStaff } from "../staff/useStaff";
import {
  LANDING_BLOCKS, LANDING_THEMES, landingBlocksFor,
  LandingPreview, BlockInspector,
  findBlockDeep, parentRowOf, updateCfgDeep, removeBlockDeep, moveBlockDeep,
  insertBeforeDeep, appendToCellDeep, getRowCells, CELL_FORBIDDEN,
  cloneBlockDeep, insertAfterDeep,
  type LandingBlock, type LandingTheme,
} from "../landing/landing-builder-helpers";
import { QPAGE_META, type QPageId } from "./qp-pages";
import { QUEUE_BLOCKS, isQueueBlock, queueDesignDefault, qSampleRuntime, QueueBlockView, QueueBlockInspector, qResolveQuestionSections, type QQuestionLib, type QQuestionRef, type QRuntime } from "./qblocks";
import type { QPickSurvey } from "./q-event-pickers";
import { useQuestions } from "../questions/useQuestions";
import { useSurveys } from "../surveys/SurveyEditorPage";
import { useSurveyQuestions } from "../surveys/survey-questions-store";

import { useT } from "@v0y4ge/i18n-runtime";
/** Event-landing blocks that make no sense on queue journey pages — hidden
 *  from THIS builder's palette only (operator request 2026-07-22). Existing
 *  designs already containing one still render; it just can't be added anew. */
const QP_HIDDEN_BLOCKS = new Set(["speakers", "agenda", "tickets", "map", "signup", "countdown", "sponsors", "stats", "social", "dates", "nav", "chat"]); // ui-config

export interface QPageEditEntry { stepId: string; page: QPageId; cfg: Record<string, unknown> }
interface QPageDoc { blocks: LandingBlock[]; theme: string; font: string }

function brandTheme(branding?: { primary?: string; cover?: string; bodyFont?: string }): LandingTheme {
  return { id: "brand", name: "Brand", brand: branding?.primary || T.p1, hero: [branding?.primary || "#75ADFA", branding?.cover || T.p1], font: branding?.bodyFont || "DM Sans" };
}

/** Landing-style doc for a page — its saved design, else a default seeded from
 * the page's legacy cfg (hero + the page's own FUNCTIONAL block). */
function docFor(entry: QPageEditEntry, heroUrl?: string): QPageDoc {
  const d = (entry.cfg as { design?: { blocks?: LandingBlock[]; theme?: string; font?: string } }).design;
  if (d && Array.isArray(d.blocks) && d.blocks.length) return { blocks: d.blocks, theme: d.theme || "brand", font: d.font || "DM Sans" };
  return { blocks: queueDesignDefault(entry.page, entry.cfg, heroUrl), theme: "brand", font: "DM Sans" };
}

export function QueuePageBuilder({ pages, startStepId, onDone, onCancel, runtime }:
  { pages: QPageEditEntry[]; startStepId?: string; onDone: (cfgs: Record<string, Record<string, unknown>>) => void; onCancel: () => void; runtime?: Partial<QRuntime> }) {
  const t = useT();
  const branding = useBranding();
  const staffStore = useStaff();
  const staffMembers = (staffStore.members as unknown as Record<string, unknown>[]).map((m) => ({ id: String(m.id), name: String(m.name || [m.firstName, m.lastName].filter(Boolean).join(" ") || "Staff"), email: m.email ? String(m.email) : undefined }));
  const staffGroups = (staffStore.groups as unknown as Record<string, unknown>[]).map((g) => ({ id: String(g.id), name: String(g.name || "Group") }));

  // One doc per journey page, keyed by step id. Edits are local until Done.
  const [docs, setDocs] = useState<Record<string, QPageDoc>>(() => Object.fromEntries(pages.map((p) => [p.stepId, docFor(p, runtime?.heroImage)])));
  const [dirty, setDirty] = useState<Set<string>>(() => new Set());
  const startIdx = Math.max(0, pages.findIndex((p) => p.stepId === startStepId));
  const [cur, setCur] = useState(startIdx);
  const entry = pages[Math.min(cur, pages.length - 1)];
  const meta = QPAGE_META[entry.page];
  const doc = docs[entry.stepId];
  const blocks = doc.blocks;
  const themeId = doc.theme;
  const font = doc.font;

  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  // Builder-only simulation of the Waiting page's called state ("It's your
  // turn") — lifted to the builder so block remounts can't reset it.
  const [simCalled, setSimCalled] = useState(false);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [past, setPast] = useState<{ stepId: string; doc: QPageDoc }[]>([]);
  const [future, setFuture] = useState<typeof past>([]);
  const dragRef = useRef<{ type: string | null; uid: string | null }>({ type: null, uid: null });

  const markDirty = (stepId: string) => setDirty((d) => { if (d.has(stepId)) return d; const n = new Set(d); n.add(stepId); return n; });
  const patchDoc = (patch: Partial<QPageDoc>) => { setDocs((d) => ({ ...d, [entry.stepId]: { ...d[entry.stepId], ...patch } })); markDirty(entry.stepId); };
  const setBlocks = (next: LandingBlock[] | ((prev: LandingBlock[]) => LandingBlock[])) => {
    setDocs((d) => { const cd = d[entry.stepId]; return { ...d, [entry.stepId]: { ...cd, blocks: typeof next === "function" ? next(cd.blocks) : next } }; });
    markDirty(entry.stepId);
  };

  // Page navigation — header arrows move between the journey's pages.
  const goPage = (dir: 1 | -1) => { const n = cur + dir; if (n < 0 || n >= pages.length) return; setCur(n); setSelectedUid(null); setDragOver(null); };

  // Responsive canvas — fluid width, edge drag-to-resize (no device frame).
  const [vw, setVw] = useState<number | null>(null);
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const pageColRef = useRef<HTMLDivElement>(null);
  const [liveW, setLiveW] = useState(0);
  const [draggingEdge, setDraggingEdge] = useState(false);
  const startEdgeDrag = (side: 1 | -1) => (e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX;
    const startW = pageColRef.current?.getBoundingClientRect().width || 720;
    const maxW = Math.max(360, (canvasScrollRef.current?.clientWidth || 1200) - 56);
    setDraggingEdge(true);
    const move = (ev: PointerEvent) => { const next = Math.round(startW + (ev.clientX - startX) * side * 2); setVw(Math.min(maxW, Math.max(320, next))); setLiveW(Math.round(pageColRef.current?.getBoundingClientRect().width || next)); };
    const up = () => { setDraggingEdge(false); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const currentTheme: LandingTheme = themeId === "brand" ? brandTheme(branding) : (LANDING_THEMES.find((t) => t.id === themeId) || brandTheme(branding));
  // Sample runtime, overlaid with the REAL queue context when the builder is
  // opened from a queue (its selected services/groups) — the preview lists ALL
  // of them and the page grows to fit.
  const sampleRt = useMemo(() => ({ ...qSampleRuntime(), ...(runtime ?? {}) }), [runtime]);
  const qlib = useQuestions();
  const questionLib: QQuestionLib = useMemo(() => {
    const toQ = (q: { id: string; label: string; type?: string; options?: { label: string }[] }) => ({ id: q.id, label: q.label, options: q.options?.length ? q.options.map((o) => o.label) : undefined });
    const qs = qlib.questions().filter((q) => q.label);
    return {
      questions: qs.map(toQ),
      groups: qlib.groups().filter((g) => g.name).map((g) => ({
        id: g.id, name: g.name, count: g.items.length,
        questions: g.items.map((it) => { const q = qs.find((x) => x.id === it.qid); return q ? toQ(q) : null; }).filter(Boolean) as { id: string; label: string; options?: string[] }[],
      })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qlib.loading, qlib.questions().length, qlib.groups().length]);
  /** Canvas runtime per block — the qcontact/qprebook blocks preview their own
   *  picked question refs; qsurvey/qnoservice preview their picked survey. */
  const { surveys } = useSurveys();
  const sqStore = useSurveyQuestions(); // survey items reference the SURVEY-question library, not booking questions
  const surveyLib: QPickSurvey[] = surveys.map((s) => ({ id: s.id, name: s.name, count: (s.items || []).length, description: s.description }));
  const svResolve = (id?: string | null) => {
    if (!id) return null;
    const s = surveys.find((x) => x.id === id);
    if (!s) return null;
    const qs = sqStore.questions();
    return {
      id: s.id, name: s.name,
      questions: (s.items || []).map((it: { qid: string }) => { const q = qs.find((x) => x.id === it.qid); return q ? { id: q.id, label: q.label, type: (/rating|scale|nps/i.test(String((q as { type?: string }).type || "")) ? "rating" : "text") as "rating" | "text" } : null; }).filter(Boolean) as { id: string; label: string; type: "rating" | "text" }[],
    };
  };
  const rtFor = (b: LandingBlock) => b.type === "qcontact" || b.type === "qprebook"
    ? { ...sampleRt, questionSections: qResolveQuestionSections((b.cfg as { questions?: QQuestionRef[] }).questions, questionLib) }
    : b.type === "qsurvey" || b.type === "qnoservice"
    ? { ...sampleRt, survey: svResolve((b.cfg as { surveyId?: string | null }).surveyId) }
    : b.type === "qstatus" && simCalled
    // Builder-only "Simulate It's Your Turn": called state, top of the queue,
    // the your-turn message in the thread. Lives HERE (not in the block) so a
    // wizard re-render / autosave can't reset it mid-walkthrough.
    ? { ...sampleRt, entryStatus: "called" as const, position: 1, messages: [...sampleRt.messages, { from: "team", text: "It's your turn — please come up now." }] }
    : sampleRt;
  const selected = findBlockDeep(blocks, selectedUid);
  const blockDef = selected ? (LANDING_BLOCKS.find((d) => d.id === selected.type) ?? QUEUE_BLOCKS.find((d) => d.id === selected.type)) : null;

  const pushHistory = () => { const snap = { stepId: entry.stepId, doc }; setPast((p) => [...p.slice(-40), snap]); setFuture([]); };
  const applySnap = (s: { stepId: string; doc: QPageDoc }) => {
    setDocs((d) => ({ ...d, [s.stepId]: s.doc }));
    markDirty(s.stepId);
    const idx = pages.findIndex((p) => p.stepId === s.stepId);
    if (idx >= 0 && idx !== cur) { setCur(idx); setSelectedUid(null); }
  };
  const undo = () => { if (!past.length) return; const prev = past[past.length - 1]; setFuture((f) => [{ stepId: prev.stepId, doc: docs[prev.stepId] }, ...f.slice(0, 40)]); setPast((p) => p.slice(0, -1)); applySnap(prev); };
  const redo = () => { if (!future.length) return; const next = future[0]; setPast((p) => [...p.slice(-40), { stepId: next.stepId, doc: docs[next.stepId] }]); setFuture((f) => f.slice(1)); applySnap(next); };

  const updateCfg = (uid: string, patch: Record<string, unknown>) => { pushHistory(); setBlocks(updateCfgDeep(blocks, uid, patch)); };
  // Queue functional blocks are full-width interactive sections — top-level only.
  const cellNo = (t: string) => CELL_FORBIDDEN.has(t) || isQueueBlock(t);
  const spawnBlock = (type: string): LandingBlock | null => {
    const qdef = QUEUE_BLOCKS.find((d) => d.id === type);
    if (qdef) return { uid: `b${Date.now().toString(36)}${((Math.random() * 1e6) | 0).toString(36)}`, type, cfg: { ...qdef.defaults } };
    const def = LANDING_BLOCKS.find((d) => d.id === type);
    if (!def) return null;
    const cfgVal = type === "row"
      ? { ratio: (def.defaults as { ratio?: string }).ratio || "1-1", gap: (def.defaults as { gap?: number }).gap ?? 20, stackMobile: true, cells: [[], []] as LandingBlock[][] }
      : { ...def.defaults };
    return { uid: `b${Date.now().toString(36)}${((Math.random() * 1e6) | 0).toString(36)}`, type, cfg: cfgVal };
  };
  const addBlock = (type: string, atIndex?: number) => {
    if ((type === "hero" || isQueueBlock(type)) && usedTypes.has(type)) return; // single-instance blocks
    const nb = spawnBlock(type);
    if (!nb) return;
    pushHistory();
    const i = atIndex == null ? blocks.length : atIndex;
    setBlocks([...blocks.slice(0, i), nb, ...blocks.slice(i)]);
    setSelectedUid(nb.uid);
  };
  const removeBlock = (uid: string) => { pushHistory(); const next = removeBlockDeep(blocks, uid); setBlocks(next); if (uid === selectedUid) setSelectedUid(null); };
  const moveBlock = (uid: string, dir: number) => { pushHistory(); setBlocks(moveBlockDeep(blocks, uid, dir)); };
  const duplicateBlock = (uid: string) => { const src = findBlockDeep(blocks, uid); if (!src) return; const clone = cloneBlockDeep(src); pushHistory(); setBlocks(insertAfterDeep(blocks, uid, clone)); setSelectedUid(clone.uid); };
  const onDropOnBlock = (targetUid: string) => {
    const src = dragRef.current;
    dragRef.current = { type: null, uid: null };
    setDragOver(null);
    const intoCell = !!parentRowOf(blocks, targetUid);
    if (src.type) {
      if (intoCell && cellNo(src.type)) return;
      const nb = spawnBlock(src.type);
      if (!nb) return;
      pushHistory();
      setBlocks((prev) => insertBeforeDeep(prev, targetUid, nb));
      setSelectedUid(nb.uid);
    } else if (src.uid && src.uid !== targetUid) {
      const found = findBlockDeep(blocks, src.uid);
      if (!found) return;
      if (intoCell && cellNo(found.type)) return;
      if (found.type === "row" && findBlockDeep(([] as LandingBlock[]).concat(...getRowCells(found.cfg)), targetUid)) return;
      pushHistory();
      setBlocks((prev) => insertBeforeDeep(removeBlockDeep(prev, src.uid!), targetUid, found));
      setSelectedUid(found.uid);
    }
  };
  const onDropToCell = (rowUid: string, cellIdx: number) => {
    const src = dragRef.current;
    dragRef.current = { type: null, uid: null };
    setDragOver(null);
    if (src.type) {
      if (cellNo(src.type)) return;
      const nb = spawnBlock(src.type);
      if (!nb) return;
      pushHistory();
      setBlocks((prev) => appendToCellDeep(prev, rowUid, cellIdx, nb));
      setSelectedUid(nb.uid);
    } else if (src.uid && src.uid !== rowUid) {
      const found = findBlockDeep(blocks, src.uid);
      if (!found || cellNo(found.type)) return;
      pushHistory();
      setBlocks((prev) => appendToCellDeep(removeBlockDeep(prev, src.uid!), rowUid, cellIdx, found));
      setSelectedUid(found.uid);
    }
  };

  // Done returns every page's cfg keyed by step id — only dirty pages gain a
  // (new) design; untouched pages keep their cfg byte-identical. Which pages a
  // journey HAS is set at the flow level (Queue Journeys canvas / the wizard's
  // per-queue optional-page checkboxes), never here.
  const done = () => {
    const cfgs = Object.fromEntries(pages.map((p) => {
      if (!dirty.has(p.stepId)) return [p.stepId, p.cfg];
      const d = docs[p.stepId];
      return [p.stepId, { ...p.cfg, design: { blocks: d.blocks, theme: d.theme, font: d.font, brandTheme: brandTheme(branding) } }];
    }));
    onDone(cfgs);
  };

  const usedTypes = useMemo(() => {
    const set = new Set<string>();
    const collect = (list: LandingBlock[]) => list.forEach((bl) => { set.add(bl.type); if (bl.type === "row") getRowCells(bl.cfg).forEach(collect); });
    collect(blocks);
    return set;
  }, [blocks]);

  const coreDef = QUEUE_BLOCKS.find((b) => b.page === entry.page)!;
  const hasCore = usedTypes.has(coreDef.id);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, background: T.canvas, display: "flex", flexDirection: "column" }}>
      {/* Header — arrows page through the journey's pages */}
      <div style={{ padding: "11px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 11, flexShrink: 0, background: T.white }}>
        <span style={{ width: 32, height: 32, borderRadius: 8, background: meta.tint, color: meta.color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.phone size={15}/></span>
        <div style={{ flex: "0 1 auto", minWidth: 0, overflow: "hidden" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.n1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t('queues.pageBuilder.text')}</div>
          <div style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t('queues.pageBuilder.text2')}</div>
        </div>
        <div style={{ flex: "1 0 auto", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          <span style={{ flexShrink: 0, opacity: cur > 0 ? 1 : .35, display: "inline-flex" }}><IconBtn size={32} label={t('queues.pageBuilder.label')} onClick={() => goPage(-1)} testid="qpage-prev-btn"><Icon.arrow dir="left" size={14}/></IconBtn></span>
          <div style={{ textAlign: "center", minWidth: 130, flexShrink: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1, whiteSpace: "nowrap" }}>{meta.label}</div>
            <div style={{ fontSize: 11, color: T.soft, whiteSpace: "nowrap" }}>{t('queues.pageBuilder.text3')}{" "}{Math.min(cur, pages.length - 1) + 1} {t('queues.pageBuilder.text4')}{" "}{pages.length}{dirty.has(entry.stepId) ? t('queues.pageBuilder.text5') : ""}</div>
          </div>
          <span style={{ flexShrink: 0, opacity: cur < pages.length - 1 ? 1 : .35, display: "inline-flex" }}><IconBtn size={32} label={t('queues.pageBuilder.label2')} onClick={() => goPage(1)} testid="qpage-next-btn"><Icon.arrow dir="right" size={14}/></IconBtn></span>
        </div>
        {!hasCore ? (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 6px 5px 11px", borderRadius: 999, background: "#FAF1DE", border: "1px solid #C8861E", color: "#8A5B12", fontSize: 11.5, fontWeight: 600, flexShrink: 0 }}>
            <Icon.warn size={12}/>{t('queues.pageBuilder.text6')}{coreDef.l}{t('queues.pageBuilder.text7')}<button onClick={() => addBlock(coreDef.id)} style={{ border: "1px solid #C8861E", background: T.white, color: "#8A5B12", borderRadius: 999, padding: "2px 9px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: T.font }}>{t('queues.pageBuilder.text8')}</button>
          </span>
        ) : null}
        <Btn variant="ghost" size="sm" onClick={undo} style={{ opacity: past.length ? 1 : .4 }}>{t('queues.pageBuilder.text9')}</Btn>
        <Btn variant="ghost" size="sm" onClick={redo} style={{ opacity: future.length ? 1 : .4 }}>{t('queues.pageBuilder.text10')}</Btn>
        <div style={{ width: 1, height: 22, background: T.border }} />
        <Btn variant="ghost" onClick={onCancel}>{t('queues.pageBuilder.text11')}</Btn>
        <Btn onClick={done} testid="qpage-done-btn">{t('queues.pageBuilder.text12')}</Btn>
      </div>

      <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
        {/* Left — palette + theme */}
        <div style={{ width: 260, background: T.white, borderRight: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 18, overflow: "auto", flexShrink: 0 }}>
          <div>
            <div style={tx.label}>{t('queues.pageBuilder.text13')}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginTop: 8 }}>
              {landingBlocksFor("landing").filter((b) => !QP_HIDDEN_BLOCKS.has(b.id)).map((b) => {
                const used = usedTypes.has(b.id);
                return (
                  <div key={b.id} draggable onClick={() => addBlock(b.id)}
                    onDragStart={() => { dragRef.current = { type: b.id, uid: null }; }}
                    onDragEnd={() => { dragRef.current = { type: null, uid: null }; setDragOver(null); }}
                    style={{ padding: "12px 8px", border: `1px solid ${used ? T.p1 : T.border}`, borderRadius: 6, background: used ? T.p4 : T.white, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: used ? T.p1 : T.n1, fontWeight: used ? 600 : 400, position: "relative" }}>
                    {b.l}
                    {used ? <span style={{ position: "absolute", top: 4, right: 4, width: 14, height: 14, borderRadius: 999, background: T.p1, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.check size={9}/></span> : null}
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <div style={tx.label}>{t('queues.pageBuilder.text14')}</div>
            <div style={{ marginTop: 8 }}>
              <Dropdown
                options={["DM Sans", "Inter", "Source Sans Pro", "Avenir Next", "Georgia", "Helvetica Neue"].map((f) => ({ value: f, label: f }))}
                value={font}
                onChange={(v) => patchDoc({ font: v })}
                aria-label={t('queues.pageBuilder.ariaLabel')}
                style={{ gap: 0, width: "100%" }}
              />
            </div>
            <div style={{ fontSize: 11, color: T.soft, marginTop: 8, lineHeight: 1.45 }}>{t('queues.pageBuilder.text15')}</div>
          </div>
        </div>

        {/* Centre — responsive live page (no device frame) */}
        <div style={{ flex: 1, background: T.canvas, overflow: "hidden", display: "flex", flexDirection: "column", position: "relative", minWidth: 0 }}>
          {entry.page === "waiting" ? (
            // Builder chrome (never on the customer page): preview the called state.
            <button onClick={() => setSimCalled((v) => !v)} data-testid="qpage-simulate-called-btn"
              style={{ position: "absolute", top: 16, right: 24, zIndex: 5, minHeight: 36, padding: "0 14px", borderRadius: 999, border: `1.5px solid ${simCalled ? T.p1 : T.border}`, background: simCalled ? T.p4 : T.white, color: simCalled ? T.p1 : T.n1, fontSize: 12.5, fontWeight: 700, fontFamily: T.font, cursor: "pointer", boxShadow: T.shadowSm, display: "inline-flex", alignItems: "center", gap: 6 }}>
              {simCalled ? <><Icon.arrow dir="left" size={12}/>{t('queues.pageBuilder.text17')}</> : t('queues.pageBuilder.text16')}
            </button>
          ) : null}
          <div ref={canvasScrollRef} style={{ flex: 1, padding: "20px 28px", overflow: "auto" }}>
            <div ref={pageColRef} style={{ position: "relative", width: vw ?? "100%", maxWidth: "100%", margin: "0 auto", transition: draggingEdge ? "none" : "width 200ms ease" }} onDragOver={(e) => e.preventDefault()}>
              <LandingPreview blocks={blocks} theme={currentTheme} font={font} event={null}
                renderCustom={(b) => isQueueBlock(b.type) ? <QueueBlockView block={b} theme={currentTheme} rt={rtFor(b)} /> : null}
                selectedUid={selectedUid} onSelect={(uid) => setSelectedUid((c) => (c === uid ? null : uid))}
                onRemove={removeBlock} onMove={moveBlock}
                dragOver={dragOver} onDragOverBlock={(uid) => setDragOver(uid)}
                onDrop={onDropOnBlock} onCellDrop={onDropToCell} onDuplicate={duplicateBlock}
                onDragStartBlock={(uid) => { dragRef.current = { type: null, uid }; }} />
              <div onDragOver={(e) => { e.preventDefault(); setDragOver("end"); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={() => {
                  const src = dragRef.current;
                  dragRef.current = { type: null, uid: null };
                  setDragOver(null);
                  if (src.type) { addBlock(src.type); return; }
                  if (src.uid) { const f = findBlockDeep(blocks, src.uid); if (f) { pushHistory(); setBlocks((prev) => [...removeBlockDeep(prev, src.uid!), f]); setSelectedUid(f.uid); } }
                }}
                style={{ marginTop: 12, padding: "20px 16px", borderRadius: 8, border: `2px dashed ${dragOver === "end" ? T.p1 : T.n4}`, background: dragOver === "end" ? T.p4 : "transparent", textAlign: "center", color: T.soft, fontSize: 13 }}>
                <Icon.plus size={14}/> {t('queues.pageBuilder.text18')}</div>
              {([-1, 1] as const).map((side) => (
                <div key={side} onPointerDown={startEdgeDrag(side)} title={t('queues.pageBuilder.title')}
                  style={{ position: "absolute", top: 0, bottom: 0, [side === -1 ? "left" : "right"]: -22, width: 20, display: "flex", alignItems: "center", justifyContent: "center", cursor: "ew-resize", touchAction: "none" } as React.CSSProperties}>
                  <span style={{ width: 5, height: 48, borderRadius: 999, background: draggingEdge ? T.p1 : T.n4, transition: "background 120ms" }} />
                </div>
              ))}
              {draggingEdge ? (
                <div style={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", zIndex: 5, background: T.n1, color: T.white, fontSize: 11, fontFamily: T.fontMono, borderRadius: 4, padding: "3px 8px", pointerEvents: "none" }}>{liveW}{t('queues.pageBuilder.text19')}</div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Right — page text + inspector + outline */}
        <div style={{ width: 300, background: T.white, borderLeft: `1px solid ${T.border}`, padding: 16, display: "flex", flexDirection: "column", gap: 14, overflow: "auto", flexShrink: 0 }}>
          {(() => {
            // Page heading/subhead — always editable here, for every page: binds
            // to the hero block when the page has one, else the functional core.
            const heroB = blocks.find((b) => b.type === "hero");
            const coreB = blocks.find((b) => isQueueBlock(b.type));
            const target = heroB ?? coreB;
            if (!target) return null;
            const hv = heroB ? String((heroB.cfg as Record<string, unknown>).headline ?? "") : String((coreB!.cfg as Record<string, unknown>).title ?? "");
            const sv = heroB ? String((heroB.cfg as Record<string, unknown>).subhead ?? "") : String((coreB!.cfg as Record<string, unknown>).sub ?? "");
            const subOk = !!heroB || (coreB ? "sub" in (QUEUE_BLOCKS.find((d) => d.id === coreB.type)?.defaults ?? {}) : false);
            const setTxt = (k: "h" | "s", v: string) => {
              if (heroB) updateCfg(heroB.uid, k === "h" ? { headline: v } : { subhead: v });
              else if (coreB) updateCfg(coreB.uid, k === "h" ? { title: v } : { sub: v });
            };
            const inp: React.CSSProperties = { width: "100%", minHeight: 36, padding: "0 10px", border: `1px solid ${T.n4}`, borderRadius: 6, fontSize: 13, fontFamily: T.font, boxSizing: "border-box" };
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={tx.label}>{t('queues.pageBuilder.text20')}</div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.n1, marginBottom: 4 }}>{t('queues.pageBuilder.text21')}</div>
                  <input value={hv} onChange={(e) => setTxt("h", e.target.value)} style={inp} data-testid="qpage-heading-input" />
                </div>
                {subOk ? (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.n1, marginBottom: 4 }}>{t('queues.pageBuilder.text22')}</div>
                    <textarea value={sv} onChange={(e) => setTxt("s", e.target.value)} rows={2} style={{ ...inp, padding: 8, resize: "vertical", minHeight: 48 }} data-testid="qpage-subheading-input" />
                  </div>
                ) : null}
                <hr style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "2px 0" }} />
              </div>
            );
          })()}
          {selected && blockDef && !isQueueBlock(selected.type) ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={tx.label}>{t('queues.pageBuilder.text23')}{blockDef.l}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  <IconBtn size={28} label={t('queues.pageBuilder.label3')} onClick={() => moveBlock(selected.uid, -1)}><Icon.chev dir="up" size={12}/></IconBtn>
                  <IconBtn size={28} label={t('queues.pageBuilder.label4')} onClick={() => moveBlock(selected.uid, +1)}><Icon.chev size={12}/></IconBtn>
                  <IconBtn size={28} label={t('queues.pageBuilder.label5')} onClick={() => duplicateBlock(selected.uid)}><Icon.copy size={12}/></IconBtn>
                  <IconBtn size={28} label={t('queues.pageBuilder.label6')} onClick={() => removeBlock(selected.uid)}><Icon.trash size={12}/></IconBtn>
                </div>
              </div>
              <BlockInspector type={selected.type} cfg={selected.cfg} onChange={(patch) => updateCfg(selected.uid, patch)} allBlocks={blocks} staffMembers={staffMembers} staffGroups={staffGroups} />
            </>
          ) : selected && isQueueBlock(selected.type) ? (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={tx.label}>{t('queues.pageBuilder.text23')}{QUEUE_BLOCKS.find((d) => d.id === selected.type)?.l}</div>
                <div style={{ display: "flex", gap: 4 }}>
                  <IconBtn size={28} label={t('queues.pageBuilder.label3')} onClick={() => moveBlock(selected.uid, -1)}><Icon.chev dir="up" size={12}/></IconBtn>
                  <IconBtn size={28} label={t('queues.pageBuilder.label4')} onClick={() => moveBlock(selected.uid, +1)}><Icon.chev size={12}/></IconBtn>
                  <IconBtn size={28} label={t('queues.pageBuilder.label6')} onClick={() => removeBlock(selected.uid)}><Icon.trash size={12}/></IconBtn>
                </div>
              </div>
              <div style={{ fontSize: 11, color: T.soft, lineHeight: 1.45, padding: "8px 10px", background: "#FDFAF2", border: "1px solid #E8D9B5", borderRadius: 6 }}>{t('queues.pageBuilder.text24')}</div>
              <QueueBlockInspector type={selected.type} cfg={selected.cfg} onChange={(patch) => updateCfg(selected.uid, patch)}
                questionLib={selected.type === "qcontact" || selected.type === "qprebook" ? questionLib : undefined}
                surveyLib={selected.type === "qsurvey" || selected.type === "qnoservice" ? surveyLib : undefined} />
            </>
          ) : (
            <div style={{ padding: 16, textAlign: "center", color: T.soft, fontSize: 13 }}>
              <div style={{ marginTop: 8 }}>{t('queues.pageBuilder.text25')}</div>
            </div>
          )}
          <hr style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "6px 0" }} />
          <div>
            <div style={tx.label}>{t('queues.pageBuilder.text26')}</div>
            <ol style={{ listStyle: "none", margin: "8px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
              {(() => {
                const rows: { b: LandingBlock; depth: number }[] = [];
                const walk = (list: LandingBlock[], depth: number) => list.forEach((b) => { rows.push({ b, depth }); if (b.type === "row") getRowCells(b.cfg).forEach((cell) => walk(cell, depth + 1)); });
                walk(blocks, 0);
                return rows.map(({ b, depth }, i) => {
                  const def = LANDING_BLOCKS.find((d) => d.id === b.type) ?? QUEUE_BLOCKS.find((d) => d.id === b.type);
                  return (
                    <li key={b.uid} onClick={() => setSelectedUid((c) => (c === b.uid ? null : b.uid))}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", paddingLeft: 8 + depth * 14, borderRadius: 4, background: selectedUid === b.uid ? T.p4 : "transparent", cursor: "pointer", border: selectedUid === b.uid ? `1px solid ${T.p2}` : "1px solid transparent" }}>
                      {depth > 0 ? <span style={{ color: T.n4, fontSize: 11, fontFamily: T.fontMono }}>&#8627;</span> : null}
                      <span style={{ fontSize: 13, color: T.n1, flex: 1, fontWeight: b.type === "row" ? 600 : 400 }}>{def?.l || b.type}</span>
                      <span style={{ fontSize: 10, color: T.soft, fontFamily: T.fontMono }}>{i + 1}</span>
                    </li>
                  );
                });
              })()}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
